import { useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import RecoveryLayout from '../../components/RecoveryLayout';
import PageLoader from '../../components/PageLoader';
import axiosInstance from '../../utils/axiosInstance';

const METHOD_OPTIONS = ['all', 'cash', 'bank_transfer', 'cheque', 'card', 'online'];

export default function Payments({ pageTitle, section }) {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [methodFilter, setMethodFilter] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [page, setPage] = useState(1);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    studentName: '',
    studentNumber: '',
    amount: '',
    method: 'cash',
    reference: '',
    date: new Date().toISOString().split('T')[0],
    notes: '',
  });
  const [saving, setSaving] = useState(false);
  const PAGE_SIZE = 20;

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const resp = await axiosInstance.get('/admin/recovery-payments');
      setPayments(resp?.data?.payments ?? []);
    } catch {
      setPayments([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPayments(); }, []);

  const filtered = payments.filter((p) => {
    const matchSearch =
      !search ||
      (p.studentName || '').toLowerCase().includes(search.toLowerCase()) ||
      (p.studentNumber || '').toLowerCase().includes(search.toLowerCase()) ||
      (p.reference || '').toLowerCase().includes(search.toLowerCase());
    const matchMethod = methodFilter === 'all' || p.method === methodFilter;
    const matchStart = !startDate || new Date(p.date) >= new Date(startDate);
    const matchEnd = !endDate || new Date(p.date) <= new Date(endDate);
    return matchSearch && matchMethod && matchStart && matchEnd;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const totalFiltered = filtered.reduce((s, p) => s + (Number(p.amount) || 0), 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.studentNumber || !form.amount) {
      Swal.fire({ icon: 'warning', title: 'Missing Fields', text: 'Student number and amount are required.' });
      return;
    }
    try {
      setSaving(true);
      await axiosInstance.post('/admin/recovery-payments', {
        ...form,
        amount: parseFloat(form.amount),
      });
      Swal.fire({ icon: 'success', title: 'Payment Recorded', timer: 1500, showConfirmButton: false });
      setForm({ studentName: '', studentNumber: '', amount: '', method: 'cash', reference: '', date: new Date().toISOString().split('T')[0], notes: '' });
      setShowForm(false);
      fetchPayments();
    } catch {
      Swal.fire({ icon: 'error', title: 'Failed', text: 'Could not record payment.' });
    } finally {
      setSaving(false);
    }
  };

  const fmt = (n) =>
    typeof n === 'number' || !isNaN(Number(n))
      ? `Rs ${new Intl.NumberFormat('en-US').format(Number(n))}`
      : '—';

  if (loading) return <RecoveryLayout title={pageTitle || 'Payments'} currentSection={section}><PageLoader /></RecoveryLayout>;

  return (
    <RecoveryLayout title={pageTitle || 'Payments'} currentSection={section}>
      {/* Action bar */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
          flexWrap: 'wrap',
          gap: 12,
        }}
      >
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', flex: 1 }}>
          <input
            type="text"
            placeholder="Search by name, student no. or reference…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            style={{ flex: 1, minWidth: 200, padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, outline: 'none', background: '#fff' }}
          />
          <select
            value={methodFilter}
            onChange={(e) => { setMethodFilter(e.target.value); setPage(1); }}
            style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, background: '#fff', cursor: 'pointer' }}
          >
            {METHOD_OPTIONS.map((m) => (
              <option key={m} value={m}>{m === 'all' ? 'All Methods' : m.replace('_', ' ').replace(/\b\w/g, (c) => c.toUpperCase())}</option>
            ))}
          </select>
          <input type="date" value={startDate} onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
            style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, background: '#fff' }} />
          <input type="date" value={endDate} onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
            style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, background: '#fff' }} />
        </div>
        <button
          onClick={() => setShowForm((v) => !v)}
          style={{ padding: '9px 18px', borderRadius: 8, border: 'none', background: '#dc2626', color: '#fff', fontWeight: 600, fontSize: 14, cursor: 'pointer' }}
        >
          {showForm ? 'Cancel' : '+ Record Payment'}
        </button>
      </div>

      {/* Add payment form */}
      {showForm && (
        <form
          onSubmit={handleSubmit}
          style={{ background: '#fff', borderRadius: 12, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.07)', marginBottom: 24 }}
        >
          <h3 style={{ fontSize: 15, fontWeight: 600, color: '#0f172a', marginBottom: 16 }}>Record New Payment</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16 }}>
            {[
              { label: 'Student Name', key: 'studentName', type: 'text' },
              { label: 'Student Number *', key: 'studentNumber', type: 'text' },
              { label: 'Amount (Rs) *', key: 'amount', type: 'number' },
              { label: 'Reference No.', key: 'reference', type: 'text' },
              { label: 'Date *', key: 'date', type: 'date' },
            ].map(({ label, key, type }) => (
              <div key={key}>
                <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4 }}>{label}</label>
                <input
                  type={type}
                  value={form[key]}
                  onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
                  style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box', outline: 'none' }}
                />
              </div>
            ))}
            <div>
              <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4 }}>Payment Method</label>
              <select
                value={form.method}
                onChange={(e) => setForm((f) => ({ ...f, method: e.target.value }))}
                style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, background: '#fff', cursor: 'pointer' }}
              >
                {METHOD_OPTIONS.filter((m) => m !== 'all').map((m) => (
                  <option key={m} value={m}>{m.replace('_', ' ').replace(/\b\w/g, (c) => c.toUpperCase())}</option>
                ))}
              </select>
            </div>
          </div>
          <div style={{ marginTop: 12 }}>
            <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4 }}>Notes</label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              rows={2}
              style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, resize: 'vertical', boxSizing: 'border-box', outline: 'none' }}
            />
          </div>
          <div style={{ marginTop: 16, display: 'flex', gap: 12 }}>
            <button
              type="submit"
              disabled={saving}
              style={{ padding: '9px 20px', borderRadius: 8, border: 'none', background: '#dc2626', color: '#fff', fontWeight: 600, fontSize: 14, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1 }}
            >
              {saving ? 'Saving…' : 'Save Payment'}
            </button>
            <button
              type="button"
              onClick={() => setShowForm(false)}
              style={{ padding: '9px 20px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#374151', fontWeight: 500, fontSize: 14, cursor: 'pointer' }}
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* Summary bar */}
      <div style={{ background: '#fff', borderRadius: 12, padding: '14px 20px', boxShadow: '0 1px 3px rgba(0,0,0,0.07)', marginBottom: 16, display: 'flex', gap: 24, flexWrap: 'wrap', fontSize: 13 }}>
        <span style={{ color: '#64748b' }}><b style={{ color: '#0f172a' }}>{filtered.length}</b> payment{filtered.length !== 1 ? 's' : ''}</span>
        <span style={{ color: '#64748b' }}>Total: <b style={{ color: '#16a34a' }}>{fmt(totalFiltered)}</b></span>
      </div>

      {/* Table */}
      <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.07)', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead style={{ background: '#f8fafc' }}>
              <tr>
                {['Student', 'Student No.', 'Amount', 'Method', 'Reference', 'Date', 'Notes'].map((h) => (
                  <th key={h} style={{ textAlign: 'left', padding: '12px 16px', color: '#64748b', fontWeight: 600, borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paginated.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ padding: '32px 16px', textAlign: 'center', color: '#94a3b8' }}>No payment records found.</td>
                </tr>
              ) : (
                paginated.map((row, i) => (
                  <tr key={row.paymentId || i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '10px 16px', color: '#0f172a', fontWeight: 500 }}>{row.studentName || '—'}</td>
                    <td style={{ padding: '10px 16px', color: '#64748b' }}>{row.studentNumber || '—'}</td>
                    <td style={{ padding: '10px 16px', color: '#16a34a', fontWeight: 600 }}>{fmt(row.amount)}</td>
                    <td style={{ padding: '10px 16px' }}>
                      <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600, background: '#f1f5f9', color: '#334155' }}>
                        {(row.method || '—').replace('_', ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                      </span>
                    </td>
                    <td style={{ padding: '10px 16px', color: '#64748b' }}>{row.reference || '—'}</td>
                    <td style={{ padding: '10px 16px', color: '#64748b', whiteSpace: 'nowrap' }}>
                      {row.date ? new Date(row.date).toLocaleDateString('en-GB') : '—'}
                    </td>
                    <td style={{ padding: '10px 16px', color: '#64748b', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {row.notes || '—'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: 8, padding: '16px', borderTop: '1px solid #e2e8f0' }}>
            <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}
              style={{ padding: '6px 14px', borderRadius: 6, border: '1px solid #d1d5db', background: page === 1 ? '#f9fafb' : '#fff', color: page === 1 ? '#9ca3af' : '#374151', cursor: page === 1 ? 'not-allowed' : 'pointer', fontSize: 13 }}>
              Previous
            </button>
            <span style={{ fontSize: 13, color: '#64748b', alignSelf: 'center' }}>Page {page} of {totalPages}</span>
            <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}
              style={{ padding: '6px 14px', borderRadius: 6, border: '1px solid #d1d5db', background: page === totalPages ? '#f9fafb' : '#fff', color: page === totalPages ? '#9ca3af' : '#374151', cursor: page === totalPages ? 'not-allowed' : 'pointer', fontSize: 13 }}>
              Next
            </button>
          </div>
        )}
      </div>
    </RecoveryLayout>
  );
}
